import React, { useMemo, useState } from 'react';
import { DownloadCloud, Pencil, Plus, Trash2, UploadCloud } from 'lucide-react';
import { Button } from '../../../components/ui/Button/Button';
import { ConfirmDialog } from '../../../components/technician/ui/ConfirmDialog';
import { EmptyNote } from '../../../components/technician/ui/EmptyNote';
import { PanelSection } from '../../../components/technician/ui/PanelSection';
import { TONE, TONE_CHIP } from '../../../lib/rcu/catalog/feedback';
import type { Tone } from '../../../lib/rcu/catalog/feedback';
import { KEYPAD_ADDRESS_MODE, KEYPAD_FAMILY_SPECS, hasSelectableAddress } from '../catalog/family';
import { KEYPAD_LIMITS } from '../catalog/limits';
import { DEVICE_ANSWER } from '../catalog/status';
import { KEYPAD_TYPE_SPECS } from '../catalog/type';
import { encodeKeypad, triggerOf, triggersOf } from '../encode';
import { familyOf, sortedKeypads } from '../model';
import type { Keypad, KeypadKey } from '../model';
import { eraseTriggers, readPanel, writePanel } from '../port';
import type { KeypadPort } from '../port';
import { erasePlan, keyStateReports } from '../stored';
import { useKeypadStore } from '../store';
import type { MappingTarget } from '../targets';
import type { KeypadLink, KeypadReply } from '../reply';
import { validatePanel } from '../validate';
import { FamilyCaveat } from './FamilyCaveat';
import { IssueList } from './IssueList';
import { KeyEditor } from './KeyEditor';
import { KeypadCanvas } from './KeypadCanvas';
import { KeypadEditorSheet } from './KeypadEditorSheet';
import { KeypadList } from './KeypadList';
import type { PanelSummary } from './KeypadList';
import { countOf, pronounFor, verbFor } from './count';
import { DeviceReply } from './DeviceReply';
import { PayloadPreview } from './PayloadPreview';
import { RemoveKeypadSheet } from './RemoveKeypadSheet';

export interface KeypadsPanelProps {
  /** The outputs the current RCU configuration offers. */
  readonly targets: readonly MappingTarget[];
  /** How this console reaches the device. */
  readonly port: KeypadPort;
  /**
   * The last thing the device said about a mapping, as text.
   *
   * The firmware does not tie a reply to the request that caused it, so this is
   * shown for what it is — the newest word from the device — rather than being
   * claimed as the answer to the write the technician just sent.
   */
  readonly reply: KeypadReply | null;
  /**
   * What the console's link knows about itself, for when `reply` is null.
   *
   * Silence is not evidence: the device may have said nothing, or the tablet may
   * not have been listening. These three facts are what tell the two apart.
   */
  readonly link: KeypadLink;
}

/** What the panel is doing right now, so one action runs at a time. */
type PanelWork = 'write' | 'read' | 'erase' | null;

/**
 * What a write is about to do, in full.
 *
 * A panel with a state that cannot be written is still worth writing: the states
 * that are complete go, and the ones that would be wrong stay behind. Saying how
 * many are left out is the difference between a partial write and a surprise.
 */
const writeMessage = (name: string, states: number, blocking: number): string => {
  const base = `This sends ${countOf(states, 'key state', 'key states')} of ${name} to the RCU as one document, and stores ${pronounFor(states)} in the RCU's memory.`;
  return blocking === 0
    ? base
    : `${base} ${countOf(blocking, 'state', 'states')} ${verbFor(blocking)} left out: ${pronounFor(blocking)} point at outputs the configuration does not offer.`;
};

/** How a panel's address reads in its header. */
const addressSummary = (keypad: Keypad): string => {
  const spec = KEYPAD_FAMILY_SPECS[familyOf(keypad)];
  if (spec.addressMode === KEYPAD_ADDRESS_MODE.FIXED) return 'fixed addresses';
  return keypad.address === null ? 'no address yet' : `address ${keypad.address}`;
};

/**
 * The keypad half of the technician console: what the panels are, and what their
 * keys do.
 *
 * The panel is the unit of work here, deliberately. A technician stands at a
 * door with one keypad in front of them: they program its keys, send the whole
 * panel in one document, read it back, and move on. Nothing on this screen
 * depends on the guest console, the rooms or the link being up — the book lives
 * on the tablet, and the device is something the panel writes *to* when asked.
 */
export const KeypadsPanel: React.FC<KeypadsPanelProps> = ({ targets, port, reply, link }) => {
  const book = useKeypadStore((store) => store.book);
  const answers = useKeypadStore((store) => store.answers);
  const notice = useKeypadStore((store) => store.notice);
  const clearNotice = useKeypadStore((store) => store.clearNotice);
  const setNotice = useKeypadStore((store) => store.setNotice);
  const startReading = useKeypadStore((store) => store.startReading);
  const finishReading = useKeypadStore((store) => store.finishReading);
  const noteWritten = useKeypadStore((store) => store.noteWritten);
  const forgetWritten = useKeypadStore((store) => store.forgetWritten);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [confirmingWrite, setConfirmingWrite] = useState(false);
  const [confirmingErase, setConfirmingErase] = useState(false);
  const [busy, setBusy] = useState<PanelWork>(null);

  /**
   * Every panel encoded against the outputs the configuration offers *now*, so a
   * rail that was removed shows up as a blocking issue on the panel that used it
   * rather than as a mapping that quietly stops working.
   */
  const summaries: readonly PanelSummary[] = useMemo(
    () =>
      sortedKeypads(book).map((keypad) => {
        const encoded = encodeKeypad(keypad, targets);
        const reports = keyStateReports(book, keypad, encoded, answers);
        const validation = validatePanel({ book, keypad, targets, encoded, reports });
        return {
          keypad,
          reports,
          states: validation.states,
          blocking: validation.blocking.length,
        };
      }),
    [book, targets, answers],
  );

  const selected = summaries.find((summary) => summary.keypad.id === selectedId) ?? summaries[0];
  const panel: Keypad | null = selected?.keypad ?? null;
  const key: KeypadKey | null =
    panel === null
      ? null
      : (panel.keys.find((entry) => entry.id === selectedKeyId) ?? panel.keys[0] ?? null);

  /** Everything the selected panel is shown with, or null when the book is empty. */
  const view = useMemo(() => {
    if (panel === null || selected === undefined) return null;
    const encoded = encodeKeypad(panel, targets);
    const reports = keyStateReports(book, panel, encoded, answers);
    return {
      summary: selected,
      panel,
      reports,
      validation: validatePanel({ book, keypad: panel, targets, encoded, reports }),
      payload: encoded.entries.map((entry) => entry.entry),
      eraseCount: erasePlan(book, panel, answers).length,
    };
  }, [book, panel, selected, targets, answers]);

  const say = (tone: Tone, text: string): void => setNotice({ tone, text });

  const write = (summary: PanelSummary): void => {
    setConfirmingWrite(false);
    setBusy('write');
    void writePanel({ keypad: summary.keypad, targets, port })
      .then((outcome) => {
        if (!outcome.delivered) {
          say(TONE.ERROR, `Nothing was sent: ${outcome.detail}`);
          return;
        }
        for (const state of outcome.written) noteWritten(state.triggerKey, state.fingerprint);
        say(
          TONE.SUCCESS,
          `Sent ${countOf(outcome.written.length, 'key state', 'key states')} for ${summary.keypad.name} in one document.`,
        );
      })
      .finally(() => setBusy(null));
  };

  const read = (summary: PanelSummary): void => {
    if (hasSelectableAddress(familyOf(summary.keypad)) && summary.keypad.address === null) {
      say(TONE.WARNING, 'Give this panel an address before reading it back.');
      return;
    }
    setBusy('read');
    // The run is recorded before the first read leaves, so a trigger the device
    // does not answer for is never confused with one nobody has asked about.
    startReading(triggersOf(summary.keypad).map((ref) => ref.triggerKey));
    void readPanel({ keypad: summary.keypad, port })
      .then(async (outcome) => {
        await port.pause(KEYPAD_LIMITS.READ_SETTLE_MS);
        const latest = useKeypadStore.getState().answers;
        finishReading(
          outcome.asked,
          outcome.asked.filter(
            (trigger) => (latest[trigger]?.kind ?? DEVICE_ANSWER.UNKNOWN) !== DEVICE_ANSWER.UNKNOWN,
          ),
        );
        say(
          outcome.delivered ? TONE.SUCCESS : TONE.WARNING,
          outcome.delivered
            ? `Asked the device for ${countOf(outcome.asked.length, 'key state', 'key states')} and kept what it answered.`
            : `Some reads were not delivered: ${outcome.detail}`,
        );
      })
      .finally(() => setBusy(null));
  };

  const erase = (summary: PanelSummary): void => {
    setConfirmingErase(false);
    const plan = erasePlan(book, summary.keypad, answers);
    if (plan.length === 0) return;
    setBusy('erase');
    void eraseTriggers({ triggers: plan.map((ref) => triggerOf(ref.selector, ref.state)), port })
      .then((outcome) => {
        if (!outcome.ok) {
          say(TONE.ERROR, `Nothing was erased: ${outcome.detail}`);
          return;
        }
        forgetWritten(plan.map((ref) => ref.triggerKey));
        say(
          TONE.SUCCESS,
          `Erased ${countOf(plan.length, 'mapping', 'mappings')} the device held for this panel.`,
        );
      })
      .finally(() => setBusy(null));
  };

  return (
    <div className="space-y-4">
      {notice !== null && (
        <div
          className={`flex items-start justify-between gap-3 rounded-2xl px-5 py-3 text-sm ${TONE_CHIP[notice.tone]}`}
        >
          <span>{notice.text}</span>
          <button type="button" onClick={clearNotice} className="font-medium hover:underline">
            Dismiss
          </button>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[20rem_minmax(0,1fr)]">
        <PanelSection
          title="Panels"
          description="Keypads, corridor panels and readers on this RCU."
          action={
            <Button
              variant="secondary"
              size="sm"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => setAdding(true)}
            >
              Add
            </Button>
          }
        >
          <KeypadList
            book={book}
            summaries={summaries}
            selectedId={panel?.id ?? null}
            onSelect={(chosen) => {
              setSelectedId(chosen.id);
              setSelectedKeyId(null);
            }}
            onAdd={() => setAdding(true)}
          />
        </PanelSection>

        {view === null ? (
          <PanelSection title="Keys" description="Nothing to program until a panel is added.">
            <EmptyNote message="Add the panels fitted to this RCU, then map what each key does on press and on release." />
          </PanelSection>
        ) : (
          <div className="space-y-4">
            <PanelSection
              title={view.panel.name.trim() === '' ? 'Unnamed panel' : view.panel.name}
              description={`${KEYPAD_TYPE_SPECS[view.panel.type].label} · ${addressSummary(view.panel)} · ${countOf(view.validation.states, 'key state', 'key states')} to write`}
              action={
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<Pencil className="h-4 w-4" />}
                    onClick={() => setEditing(true)}
                  >
                    Details
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<DownloadCloud className="h-4 w-4" />}
                    disabled={busy !== null}
                    onClick={() => read(view.summary)}
                  >
                    {busy === 'read' ? 'Reading…' : 'Read from device'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<Trash2 className="h-4 w-4" />}
                    disabled={busy !== null || view.eraseCount === 0}
                    onClick={() => setConfirmingErase(true)}
                  >
                    Erase on device
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<Trash2 className="h-4 w-4" />}
                    disabled={busy !== null}
                    onClick={() => setRemoving(true)}
                  >
                    Remove
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    icon={<UploadCloud className="h-4 w-4" />}
                    disabled={busy !== null || !view.validation.writeable}
                    onClick={() => setConfirmingWrite(true)}
                  >
                    {busy === 'write' ? 'Sending…' : 'Write to device'}
                  </Button>
                </div>
              }
            >
              <div className="space-y-4">
                <FamilyCaveat keypad={view.panel} />
                <KeypadCanvas
                  keypad={view.panel}
                  reports={view.reports}
                  selectedKeyId={key?.id ?? null}
                  onSelectKey={(chosen) => setSelectedKeyId(chosen.id)}
                />
                {key !== null && (
                  <KeyEditor
                    keypad={view.panel}
                    keypadKey={key}
                    reports={view.reports}
                    targets={targets}
                  />
                )}
              </div>
            </PanelSection>

            <PanelSection
              title="Before writing"
              description="What stands in the way, and the exact document this console would publish."
            >
              <div className="space-y-4">
                {view.validation.issues.length === 0 ? (
                  <p className="text-sm text-[#666666]">
                    Nothing to report: every key state can be written as it stands.
                  </p>
                ) : (
                  <IssueList issues={view.validation.issues} />
                )}
                <details>
                  <summary className="cursor-pointer text-sm font-medium text-[#D4A054]">
                    Show the payload ({countOf(view.payload.length, 'entry', 'entries')})
                  </summary>
                  <div className="mt-3">
                    <PayloadPreview entries={view.payload} />
                  </div>
                </details>
              </div>
            </PanelSection>
          </div>
        )}
      </div>

      <PanelSection
        title="Last reply from the device"
        description="The newest mapping message the link has heard, exactly as it arrived."
      >
        <DeviceReply reply={reply} link={link} />
      </PanelSection>

      <KeypadEditorSheet
        isOpen={adding}
        keypad={null}
        onClose={() => setAdding(false)}
        onAdded={(created) => {
          setSelectedId(created.id);
          setSelectedKeyId(null);
        }}
      />

      {view !== null && (
        <>
          <KeypadEditorSheet
            isOpen={editing}
            keypad={view.panel}
            onClose={() => setEditing(false)}
            onAdded={() => setEditing(false)}
          />
          <RemoveKeypadSheet
            isOpen={removing}
            keypad={view.panel}
            port={port}
            onClose={() => {
              setRemoving(false);
              setSelectedId(null);
            }}
          />
          <ConfirmDialog
            isOpen={confirmingWrite}
            title={`Write ${countOf(view.validation.states, 'key state', 'key states')}?`}
            message={writeMessage(view.panel.name, view.validation.states, view.validation.blocking.length)}
            confirmLabel="Write to the device"
            onConfirm={() => write(view.summary)}
            onCancel={() => setConfirmingWrite(false)}
          />
          <ConfirmDialog
            isOpen={confirmingErase}
            title={`Erase ${countOf(view.eraseCount, 'mapping', 'mappings')} on the device?`}
            message={`The device is known to hold ${countOf(view.eraseCount, 'mapping', 'mappings')} for ${view.panel.name}. Erasing ${pronounFor(view.eraseCount)} cannot be undone, and its key states will read as "not on the device" until they are written again.`}
            confirmLabel="Erase on the device"
            destructive
            onConfirm={() => erase(view.summary)}
            onCancel={() => setConfirmingErase(false)}
          />
        </>
      )}
    </div>
  );
};

export default KeypadsPanel;
